import React from 'react'
import {
  Row,
  Col,
  TaskStatusTag,
  CsvFileIcon,
  DangerAlert,
  DashboardTable,
  SortFilterTh,
  formatMomentToString,
  SearchDropdown,
} from '@modifi/ui'
import {airBlue} from '@modifi/colors'
import {TaskCategory, TaskStatus} from '@modifi/enums'
import {FormattedDate, formatNumber} from '@modifi/plugin-i18n'
import {clientConfig} from '@modifi/client-config'
import {urlBuilder} from '@modifi/utilities'

import {
  ContextParametersType,
  LOCAL_STORAGE_TASK_ITEMS_PER_PAGE,
  Task,
  pageSizeOptions,
} from './constants'
import {ROUTE_COMPANY_PROFILE} from '../../routes/onboarding-routes'
import {StyledLink, FlexWrapper, StyledCaption} from '../../components/table/styles'
import DueDateIcon from './components/due-date-icon'
import {
  CompanyNameFilter,
  AssigneeFilter,
  ContextParamsSearchFilter,
} from '../../components/filters'
import Actions from './components/actions'
import ModifiUserLogo from '../../components/modifi-user-logo'
import FilterRow from './components/filter-row'
import RelatedLinks from './components/related-links'
import {fourteenDaysFromToday} from '../../utils/formatMomentToString'
import {bsrTasks, financialTasks} from '../../components/filters/TaskGroupFilter'
import {StyledStatusDiv} from './styles'
import {TranslateFn} from '../../types/i18n'

const getTaskNameTranslationProps = (
  category: TaskCategory,
  contextParams: ContextParametersType[] | []
) => {
  if (financialTasks.includes(category) && contextParams?.length > 0) {
    return {
      year: contextParams?.find(param => param?.key === 'financialYearLabel')?.value,
    }
  }

  if (bsrTasks.includes(category)) {
    let limitChangeRequestAmount
    // @ts-expect-error enum not updated
    if (category === 'TRADE_PARTNER_ALLOCATED_LIMIT_CHANGE_REQUEST') {
      const amount =
        contextParams?.find(param => param?.key === 'requestedAllocatedLimit.amount')?.value || ''
      const currency = contextParams?.find(
        param => param?.key === 'requestedAllocatedLimit.currency'
      )?.value

      limitChangeRequestAmount = formatNumber(amount, {currency, currencyDisplay: 'symbol'})
    }
    return {
      tradePartnerCompanyName:
        contextParams?.find(param => param?.key === 'tradePartnerCompanyName')?.value || '',
      ...(limitChangeRequestAmount
        ? {
            limitChangeRequestAmount,
          }
        : {}),
    }
  }
  return {}
}

export type TasksProps = {
  taskList?: Task[]
  loading: boolean
  totalCount?: number
  totalPages?: number
  updateErrorMessage?: string
  fetchTasks: () => void
  showInternalNote: (
    internalNote: string | undefined,
    internalNoteModifiedBy: string | undefined,
    taskId: string,
    canEdit: boolean
  ) => void
  t: TranslateFn
}

const Tasks: React.FC<TasksProps> = ({
  fetchTasks,
  taskList = [],
  loading,
  totalPages = 1,
  totalCount = 0,
  t,
  showInternalNote,
  updateErrorMessage = undefined,
}) => {
  const tableHeader = {
    taskName: t('tasks.table-head.task-name.label'),
    relatedLinks: (
      <SearchDropdown
        dropdownContent={ContextParamsSearchFilter}
        filterProperties={['search.contextParameters.value']}
      >
        {t('tasks.table-head.related-links.label')}
      </SearchDropdown>
    ),
    companyName: (
      <SearchDropdown
        dropdownContent={CompanyNameFilter}
        filterProperties={['filter.externalCompanyId']}
      >
        {t('tasks.table-head.company-name.label')}
      </SearchDropdown>
    ),
    dueDate: (
      <SortFilterTh property="dueDateOn" loading={loading}>
        {t('tasks.table-head.due-date.label')}
      </SortFilterTh>
    ),
    assignedTo: (
      <SearchDropdown dropdownContent={AssigneeFilter} filterProperties={['filter.assignee']}>
        {t('tasks.table-head.assigned-to.label')}
      </SearchDropdown>
    ),
    status: <StyledStatusDiv>{t('tasks.table-head.status.label')}</StyledStatusDiv>,
    notes: t('tasks.table-head.notes.label'),
    actions: t('tasks.table-head.actions.label'),
  }

  const tableDataConfig = (task: Task) => {
    const isLastItem =
      taskList &&
      taskList?.findIndex(curTask => curTask.externalId === task.externalId) ===
        taskList?.length - 1

    return {
      taskName: (
        <span data-ident={task.category}>
          {t(
            `tasks.task-name.${task.category}`,
            getTaskNameTranslationProps(
              task.category as TaskCategory,
              task.contextParameters as ContextParametersType[]
            )
          )}
        </span>
      ),
      relatedLinks:
        task?.contextParameters && task?.contextParameters.length > 0 ? (
          <RelatedLinks task={task} />
        ) : (
          '-'
        ),
      companyName: (
        <StyledLink
          pattern={ROUTE_COMPANY_PROFILE}
          to={`${clientConfig.get('SERVICE_ACTIVATIONS')}${urlBuilder(ROUTE_COMPANY_PROFILE, {
            externalCompanyId: task.externalCompanyId,
          })}`}
          scrollToTop
        >
          {task.companyName}
        </StyledLink>
      ),
      dueDate: (
        <FlexWrapper>
          <DueDateIcon dueDateValue={task.dueDateOn} taskStatus={task.status} />
          <StyledCaption ml={8} isStriked={task.status === TaskStatus.RESOLVED}>
            <FormattedDate>{task.dueDateOn}</FormattedDate>
          </StyledCaption>
        </FlexWrapper>
      ),
      assignedTo: <ModifiUserLogo email={task.assignee || ''} />,
      // @ts-expect-error
      status: <TaskStatusTag status={task.status as TaskStatus} minWidth={50} />,
      notes: task.internalNote ? (
        <CsvFileIcon
          onClick={() =>
            showInternalNote(
              task.internalNote,
              task.internalNoteModifiedBy,
              task?.externalId,
              task.status !== TaskStatus.RESOLVED
            )
          }
          color={airBlue}
          width={18}
          height={18}
          cursor="pointer"
        />
      ) : null,
      actions: <Actions task={task} isLastItem={isLastItem} />,
    }
  }

  return (
    <>
      <Row mt={48}>
        {updateErrorMessage && (
          <Col>
            <DangerAlert>{updateErrorMessage}</DangerAlert>
          </Col>
        )}
        <Col>
          <DashboardTable
            pageSizeOptions={pageSizeOptions}
            loading={loading}
            items={taskList || []}
            fetchItems={fetchTasks}
            tableStorageKey={LOCAL_STORAGE_TASK_ITEMS_PER_PAGE}
            tableHeadline={t('tasks.table.headline')}
            tableFilters={FilterRow}
            tableHeaderRow={tableHeader}
            // the table derives the row config from the same generic as `items`, but the config
            // is keyed by column instead of by task field, so it hands back a loose item here
            tableDataRowConfig={item => tableDataConfig(item as Task)}
            keyFn={task => task.name}
            defaultFilterProperties={{
              orderBy: ['dueDateOn'],
              orderDirection: ['ASC'],
              'filter.status': [TaskStatus.TODO, TaskStatus.IN_REVIEW],
              'filter.dueDateOn.to': formatMomentToString(fourteenDaysFromToday),
            }}
            totalPages={totalPages!}
            totalCount={totalCount!}
          />
        </Col>
      </Row>
    </>
  )
}

export default Tasks
